import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronRight, UserRound } from 'lucide-react';
import { Drawer } from '../../components/Drawer';
import {
  Badge,
  EmptyState,
  ErrorNote,
  Pagination,
  Skeleton,
  cn,
} from '../../components/ui';
import { api } from '../../lib/api';
import {
  formatCredits,
  formatDateTime,
  formatMoneyMinor,
  formatNumber,
  formatPlatform,
  formatRelative,
  timezoneOffsetMinutes,
} from '../../lib/format';
import { useT } from '../../lib/i18n';
import { useOpenRecord } from '../../lib/navigation';
import type {
  ActivityActor,
  ActivityFeed,
  ActivityMetric,
  ActivityScope,
  ActivityUnit,
} from '../../lib/types';

const PAGE_SIZE = 25;

/** Every metric a tile may open, and nothing else. */
const ACTIVITY_METRICS: readonly ActivityMetric[] = [
  'top_up',
  'premium',
  'revenue',
  'new_user',
  'woke_up',
  'missed',
  'forfeited',
  'payout',
  'spent',
  'adjusted',
];

/**
 * Reads a metric out of the address bar, refusing anything invented.
 *
 * Which sheet is open lives in the url so it survives a refresh and can be
 * sent to a colleague — and a url can say anything, so this is where a typo
 * stops being a 400 from the API.
 */
export function parseMetric(value: string | null): ActivityMetric | null {
  return ACTIVITY_METRICS.find((metric) => metric === value) ?? null;
}

/**
 * Who is behind one number on the dashboard.
 *
 * Every tile on the overview answers "how many"; this answers the question an
 * operator asks straight afterwards — "who?" — and then hands them to the
 * account so the third question has somewhere to go. A sheet rather than a
 * page, because the figure it decomposes should still be on screen behind it.
 *
 * Paged over *people*: someone who topped up four times this morning is one
 * row saying "4 ×", not four rows repeating the same device.
 */
export function ActivityDrawer({
  metric,
  scope,
  days,
  onClose,
}: {
  metric: ActivityMetric;
  scope: ActivityScope;
  /** The trend window, for a `window`-scoped sheet. Today's ignores it. */
  days: number;
  onClose: () => void;
}) {
  const t = useT();
  const openRecord = useOpenRecord();
  const [page, setPage] = useState(1);

  const feed = useQuery({
    queryKey: ['activity', metric, scope, days, page],
    queryFn: () =>
      api.get<ActivityFeed>('/api/dashboard/activity', {
        metric,
        scope,
        days,
        // The operator's calendar, exactly as the tile's was.
        tzOffset: timezoneOffsetMinutes(),
        page,
        limit: PAGE_SIZE,
      }),
    // Paging keeps the previous page on screen rather than flashing a
    // skeleton over a list the operator is reading.
    placeholderData: keepPreviousData,
  });

  const data = feed.data;

  return (
    <Drawer
      open
      title={t(`activity.metric.${metric}`)}
      onClose={onClose}
      footer={
        data && data.total > 0 ? (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            onChange={setPage}
          />
        ) : undefined
      }
    >
      <p className="text-xs text-ink-2">
        {t(`activity.about.${metric}`)}{' '}
        <span className="text-ink-3">
          {scope === 'today'
            ? t('activity.scope.today')
            : t('activity.scope.window', { days })}
        </span>
      </p>

      {feed.isError ? (
        <div className="mt-4">
          <ErrorNote error={feed.error} />
        </div>
      ) : !data ? (
        <div className="mt-4 space-y-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-64" />
        </div>
      ) : (
        <Feed
          feed={data}
          onOpenUser={(userId) => {
            // The sheet closes first: the operator is leaving the page it
            // belongs to, and a slide-over left hanging over a new record is
            // the one thing worse than no sheet at all.
            onClose();
            openRecord({ kind: 'user', id: userId });
          }}
        />
      )}
    </Drawer>
  );
}

function Feed({
  feed,
  onOpenUser,
}: {
  feed: ActivityFeed;
  onOpenUser: (userId: string) => void;
}) {
  const t = useT();

  return (
    <div className="mt-4 space-y-4">
      {/* What the tile said, decomposed into its three parts — so the sheet
          visibly adds up to the number that opened it. */}
      <dl
        className={cn(
          'grid gap-px overflow-hidden rounded-lg border border-line bg-line',
          // A counted metric has no third number to show: its amount *is* its
          // count, and printing it twice would only invite the reader to look
          // for the difference.
          feed.unit === 'count' ? 'grid-cols-2' : 'grid-cols-3',
        )}
      >
        <Total
          label={t('activity.total.users')}
          value={formatNumber(feed.totals.users)}
        />
        <Total
          label={t(`activity.count.${feed.metric}`)}
          value={formatNumber(feed.totals.events)}
        />
        {feed.unit !== 'count' && (
          <Total
            label={t(`activity.total.${feed.unit}`)}
            value={formatAmount(feed.totals.amount, feed.unit)}
          />
        )}
      </dl>

      {feed.items.length === 0 ? (
        <EmptyState
          icon={<UserRound size={20} />}
          title={t('activity.empty')}
          description={t('activity.emptyDescription')}
        />
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
          {feed.items.map((actor) => (
            <ActorRow
              key={actor.userId}
              actor={actor}
              unit={feed.unit}
              onOpen={() => onOpenUser(actor.userId)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface px-3 py-2.5">
      <dt className="truncate text-[11px] text-ink-2">{label}</dt>
      <dd className="tnum mt-0.5 text-base font-semibold text-ink">{value}</dd>
    </div>
  );
}

/**
 * One person's share, as a row you can walk through.
 *
 * The whole row is the target rather than a trailing link: on a phone this
 * sheet *is* the screen, and a full-width row is a great deal easier to hit at
 * six in the morning than a chevron.
 */
function ActorRow({
  actor,
  unit,
  onOpen,
}: {
  actor: ActivityActor;
  unit: ActivityUnit;
  onOpen: () => void;
}) {
  const t = useT();

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex min-h-[52px] w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-raised"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-mono text-xs text-ink">
              {actor.deviceId || actor.userId}
            </span>
            {actor.isSubscribed && (
              <Badge tone="brand">{t('activity.premium')}</Badge>
            )}
            {actor.isDeleted && (
              <Badge tone="danger">{t('activity.deleted')}</Badge>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-ink-3">
            {formatPlatform(actor.platform)}
            {actor.country && ` · ${actor.country}`}
            {' · '}
            <span title={formatDateTime(actor.lastAt)}>
              {formatRelative(actor.lastAt)}
            </span>
          </p>
        </div>

        <div className="shrink-0 text-right">
          <p className="tnum text-sm font-semibold text-ink">
            {unit === 'count'
              ? t('activity.times', { count: actor.events })
              : formatAmount(actor.amount, unit)}
          </p>
          {/* The count only earns its line where the amount is the headline
              and the two can differ. */}
          {unit !== 'count' && actor.events > 1 && (
            <p className="tnum text-xs text-ink-3">
              {t('activity.times', { count: actor.events })}
            </p>
          )}
        </div>

        <ChevronRight size={16} className="shrink-0 text-ink-3" />
      </button>
    </li>
  );
}

/** Credits and kuruş are both integers; only the unit says which. */
function formatAmount(amount: number, unit: ActivityUnit): string {
  if (unit === 'money') return formatMoneyMinor(amount);
  if (unit === 'credits') return formatCredits(amount);
  return formatNumber(amount);
}
